import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import CategoryPayment from "../components/CategoryPayment";
import "./css/WorkAmountCategories.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

export default function WorkAmountCategories() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [initialSubCategories, setInitialSubCategories] = useState([]);
  const [initialSpecialisations, setInitialSpecialisations] = useState([]);
  const [initialWorkAmount, setInitialWorkAmount] = useState(null);
  const [disabledSubCategories, setDisabledSubCategories] = useState([]);
  const [disabledSpecialisations, setDisabledSpecialisations] = useState([]);

  // Translations
  const t = {
    en: {
      generalSettings: "General Settings",
      workAmountCategories: "Work Amount & Categories",
      loading: "Loading",
      infoBanner: "If you want to add any new category you can add it here and additional charges may apply!",
      categorySelection: "Category Selection",
      categorySelectionSubtitle: "Please select the categories that you work in.",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      workAmountCategories: "مبلغ العمل والفئات",
      loading: "جاري التحميل",
      infoBanner: "إذا كنت تريد إضافة أي فئة جديدة يمكنك إضافتها هنا وقد تطبق رسوم إضافية!",
      categorySelection: "اختيار الفئة",
      categorySelectionSubtitle: "يرجى اختيار الفئات التي تعمل فيها.",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;
          
          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          // If user has already purchased sub_categories, load them
          if (
            profileData.sub_categories &&
            Array.isArray(profileData.sub_categories) &&
            profileData.sub_categories.length > 0
          ) {
            // Extract sub-category IDs
            const subCatIds = profileData.sub_categories.map((subCat) =>
              typeof subCat === "object" && subCat._id ? subCat._id : subCat.toString()
            );
            setInitialSubCategories(subCatIds);
            setDisabledSubCategories(subCatIds);
          }

          // If user has already purchased specialisations, load them
          if (
            profileData.specialisations &&
            Array.isArray(profileData.specialisations) &&
            profileData.specialisations.length > 0
          ) {
            const specIds = profileData.specialisations.map((sp) =>
              typeof sp === "object" && sp._id ? sp._id : sp.toString()
            );
            setInitialSpecialisations(specIds);
            setDisabledSpecialisations(specIds);
          }

          // If user has work_amount, load it
          if (profileData.work_amount && profileData.work_amount.min !== undefined) {
            setInitialWorkAmount({
              min: profileData.work_amount.min,
              max: profileData.work_amount.max,
            });
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || "Failed to load profile", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle payment success
  const handlePaymentSuccess = () => {
    // Reload the page to reflect changes
    window.location.reload();
  };

  // Loading screen
  if (loading) {
    return (
      <div className="work-amount-categories-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="work-amount-categories-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header with Back Button */}
      <div className="work-amount-categories-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="work-amount-categories-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="work-amount-categories-content">
        {/* Page Heading */}
        <h2 className="work-amount-categories-heading">{t.workAmountCategories}</h2>
        
        {/* Info Banner */}
        <div className="work-amount-categories-info-banner">
          <p>{t.infoBanner}</p>
        </div>

        {/* Category Selection Section */}
        <div className="category-selection-section">
          <h3 className="category-selection-heading">{t.categorySelection}</h3>
          <p className="category-selection-subtitle">{t.categorySelectionSubtitle}</p>
        </div>

        {/* Category Payment Component */}
        <CategoryPayment
          onPaymentSuccess={handlePaymentSuccess}
          initialSubCategories={initialSubCategories}
          initialSpecialisations={initialSpecialisations}
          initialWorkAmount={initialWorkAmount}
          disabledSubCategories={disabledSubCategories}
          disabledSpecialisations={disabledSpecialisations}
          lang={lang}
        />
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

